import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  House, ListDashes, Handshake, Wallet, Receipt, MapPin,
  ChartLineUp, Article, BellSimple, SignOut, Users, ShieldCheck, Plant, Truck,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { IndiaMandiLogo } from "@/components/Logo";
import api from "@/lib/api";

export default function DashboardShell() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/notifications");
        setUnread((data || []).filter((n) => !n.read).length);
      } catch {}
    })();
  }, []);

  if (user === null) {
    return <div className="min-h-screen grid place-items-center text-stone-500">Loading…</div>;
  }
  if (user === false) {
    nav("/login", { replace: true });
    return null;
  }

  const commonLinks = [
    { to: "/app", key: "dash.overview", icon: House, end: true },
    { to: "/app/listings", key: "dash.listings", icon: ListDashes },
    { to: "/app/deals", key: "dash.deals", icon: Handshake },
    { to: "/app/invoices", key: "dash.invoices", icon: Receipt },
    { to: "/app/mandis", key: "dash.mandis", icon: MapPin },
    { to: "/app/rates", key: "dash.rates", icon: ChartLineUp },
    { to: "/trade", key: "dash.trade", icon: Truck },
    { to: "/app/blog", key: "dash.blog", icon: Article },
    { to: "/app/notifications", key: "dash.notifications", icon: BellSimple },
  ];
  const userLinks = [{ to: "/app/kyc", key: "dash.kyc", icon: ShieldCheck }];
  const adminLinks = [
    { to: "/app/admin/users", key: "dash.admin_users", icon: Users },
    { to: "/app/admin/deals", key: "dash.admin_deals", icon: ShieldCheck },
    { to: "/app/admin/kyc", key: "dash.admin_kyc", icon: ShieldCheck },
  ];
  const links = user.role === "admin" ? [...commonLinks, ...adminLinks] : [...commonLinks, ...userLinks];

  const roleLabel = {
    farmer: "Verified Farmer", mandi: "Registered Mandi", trader: "Verified Trader",
    wholesaler: "Wholesaler", exporter: "Exporter", admin: "Platform Admin",
  }[user.role] || user.role;

  return (
    <div className="min-h-screen bg-stone-100 flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-emerald-950 text-emerald-100 min-h-screen sticky top-0 hidden md:flex flex-col">
        <Link to="/" className="flex items-center gap-2.5 p-5 border-b border-emerald-900">
          <IndiaMandiLogo size={38} />
          <div>
            <div className="font-display font-black text-white text-base tracking-tight">INDIA MANDI</div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-orange-400">Mandi.GLCZone.in</div>
          </div>
        </Link>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1" data-testid="sidebar-nav">
          {links.map((l) => (
            <NavLink
              key={l.to} to={l.to} end={l.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors ${
                  isActive ? "bg-emerald-800 text-white" : "text-emerald-200 hover:bg-emerald-900 hover:text-white"
                }`
              }
              data-testid={`nav-${l.key.replace(".", "-")}`}
            >
              <l.icon weight="duotone" size={20} />
              <span>{t(l.key)}</span>
              {l.key === "dash.notifications" && unread > 0 && (
                <span className="ml-auto bg-orange-500 text-white text-[10px] font-bold px-1.5 rounded-full">{unread}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-emerald-900">
          <button
            onClick={async () => { await logout(); nav("/"); }}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-semibold text-emerald-200 hover:bg-emerald-900 hover:text-white transition-colors"
            data-testid="logout-btn"
          >
            <SignOut weight="duotone" size={20} /> {t("dash.logout")}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 bg-white border-b border-stone-200">
          <div className="flex items-center justify-between px-5 sm:px-8 py-3">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">{roleLabel}</div>
              <div className="font-display text-lg font-bold text-stone-900 truncate">Namaste, {user.name.split(" ")[0]}</div>
            </div>
            <div className="flex items-center gap-3">
              <LanguageToggle />
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-50 border border-emerald-200">
                <ShieldCheck weight="fill" className="text-emerald-700" size={16} />
                <span className="text-xs font-bold text-emerald-800">{user.verified ? "KYC Verified" : "KYC Pending"}</span>
              </div>
              <div className="w-9 h-9 rounded-full bg-orange-600 text-white font-black grid place-items-center text-sm">
                {user.name.slice(0, 2).toUpperCase()}
              </div>
            </div>
          </div>
        </header>
        <main className="p-5 sm:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
